import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import React, { useEffect, useMemo, useState } from 'react';

import { getAuthenticatedHeaders, useAuthenticationService } from '@/services/authentication/authentication-service';
import { useVariable } from '@/services/backstage/BackstageProvider';
import { handleError } from '@/services/gql/config/URQLProvider';
import { useGetSupportedHolidayRegionsQuery } from '@/services/gql/graphql.generated';
import {
  ESS_BEHAVIOUR_KEYS,
  OPTIMISER_SOLVER_KEYS,
  ROSTER_SETTINGS_KEYS
} from '@/services/settings/systemSettings.keys';
import { defaultSettings } from '@/services/settings/systemSettings.types';
import { AdminSettings } from '@/types/dataManagement.types';
import strings from '../tenantSettings.translations';
import { OptionItem } from './DynamicCellRenderer';
import { GenericSettingsGrid, SettingsGridRow } from './GenericSettingsGrid';
import { isRowDataDirty } from './settingsGrid.utils';

const MINUTE_INCREMENT_KEY = 'minuteIncrement';
const MINUTE_INCREMENT_OPTIONS = [5, 15, 30];
const LEAVE_YEAR_START_MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
].map((label, index) => ({ value: index + 1, label }));

type AdminSettingsProperties = {
  [ESS_BEHAVIOUR_KEYS.allowSelfEditEmail]?: boolean;
  [ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber]?: boolean;
  [ROSTER_SETTINGS_KEYS.allowEssWorkingPatterns]?: boolean;
  [ROSTER_SETTINGS_KEYS.allowEssAvailabilityAdjustment]?: boolean;
  [OPTIMISER_SOLVER_KEYS.enabledSolvers]?: 'geneticAlgorithm' | 'cpSat' | 'both';
  [OPTIMISER_SOLVER_KEYS.enableGeneticAlgorithm]?: boolean;
  [OPTIMISER_SOLVER_KEYS.enableCpSat]?: boolean;
  [OPTIMISER_SOLVER_KEYS.showEvaluation]?: boolean;
  [MINUTE_INCREMENT_KEY]?: number;
  [key: string]: unknown;
};

type SettingsGridRowDef = SettingsGridRow & { superAdminOnly?: boolean };

const getOptimiserEnabledSolvers = (properties: AdminSettingsProperties): 'geneticAlgorithm' | 'cpSat' | 'both' => {
  const enabledSolvers = properties[OPTIMISER_SOLVER_KEYS.enabledSolvers];

  if (enabledSolvers === 'geneticAlgorithm' || enabledSolvers === 'cpSat' || enabledSolvers === 'both') {
    return enabledSolvers;
  }

  if (
    Boolean(properties[OPTIMISER_SOLVER_KEYS.enableGeneticAlgorithm]) &&
    Boolean(properties[OPTIMISER_SOLVER_KEYS.enableCpSat])
  ) {
    return 'both';
  }

  if (Boolean(properties[OPTIMISER_SOLVER_KEYS.enableCpSat])) {
    return 'cpSat';
  }

  return 'geneticAlgorithm';
};

const getMinuteIncrement = (properties: AdminSettingsProperties): number => {
  const minuteIncrement = properties[MINUTE_INCREMENT_KEY];
  return MINUTE_INCREMENT_OPTIONS.includes(minuteIncrement as number)
    ? (minuteIncrement as number)
    : defaultSettings.minuteIncrement;
};

const parseProperties = (properties?: string | null): AdminSettingsProperties => {
  if (!properties) {
    return {};
  }

  try {
    const parsed = JSON.parse(properties);
    return parsed && typeof parsed === 'object' ? (parsed as AdminSettingsProperties) : {};
  } catch {
    return {};
  }
};

// ---------------------------------------
// Register Ag-Grid Community Modules
// ---------------------------------------
ModuleRegistry.registerModules([AllCommunityModule]);

// ---------------------------------------
// Map API response to rowData
// ---------------------------------------
const mapOrganisationSettingsToRowData = (
  data: AdminSettings,
  holidayRegionOptions: OptionItem[],
  superAdmin: boolean
): SettingsGridRow[] => {
  const properties = parseProperties(data.properties);

  const rows: SettingsGridRowDef[] = [
    {
      id: 'section-leave-type',
      label: strings.admin.sectionLeaveType,
      description: strings.admin.sectionLeaveTypeDescription,
      isSection: true,
      superAdminOnly: true
    },
    {
      id: 'leaveTypeTier',
      label: strings.admin.leaveTypeTier,
      description: strings.admin.leaveTypeTierDescription,
      value: data.leaveTypeTier ?? undefined,
      type: 'select',
      options: [
        { value: 0, label: strings.admin.standard },
        { value: 1, label: strings.admin.advanced }
      ],
      isSection: false,
      superAdminOnly: true
    },
    {
      id: 'section-contract',
      label: strings.admin.contract.title,
      description: strings.admin.contract.description,
      isSection: true,
      superAdminOnly: true
    },
    {
      id: 'contractTier',
      label: strings.admin.contract.contractTier,
      description: strings.admin.contract.contractTierDescription,
      value: data.contractTier ?? undefined,
      type: 'select',
      options: [
        { value: 0, label: strings.admin.standard },
        { value: 1, label: strings.admin.advanced }
      ],
      isSection: false,
      superAdminOnly: true
    },
    {
      id: 'section-roster-settings',
      label: strings.admin.sectionRosterSettings,
      description: strings.admin.sectionRosterSettingsDescription,
      isSection: true
    },
    {
      id: MINUTE_INCREMENT_KEY,
      label: strings.admin.minuteIncrementLabel,
      description: strings.admin.minuteIncrementDescription,
      value: getMinuteIncrement(properties),
      type: 'select',
      options: MINUTE_INCREMENT_OPTIONS.map(minutes => ({ value: minutes, label: String(minutes) })),
      isSection: false
    },
    {
      id: ROSTER_SETTINGS_KEYS.allowEssWorkingPatterns,
      label: strings.admin.allowEssWorkingPatternsLabel,
      description: strings.admin.allowEssWorkingPatternsDescription,
      value: Boolean(
        properties[ROSTER_SETTINGS_KEYS.allowEssWorkingPatterns] ?? defaultSettings.allowEssWorkingPatterns
      ),
      type: 'boolean',
      isSection: false
    },
    {
      id: ROSTER_SETTINGS_KEYS.allowEssAvailabilityAdjustment,
      label: strings.admin.allowEssAvailabilityAdjustmentLabel,
      description: strings.admin.allowEssAvailabilityAdjustmentDescription,
      value: Boolean(
        properties[ROSTER_SETTINGS_KEYS.allowEssAvailabilityAdjustment] ??
        defaultSettings.allowEssAvailabilityAdjustment
      ),
      type: 'boolean',
      isSection: false
    },
    {
      id: 'section-ess-behaviour',
      label: strings.admin.sectionEssBehaviour,
      description: strings.admin.sectionEssBehaviourDescription,
      isSection: true
    },
    {
      id: ESS_BEHAVIOUR_KEYS.allowSelfEditEmail,
      label: strings.admin.allowEssEditEmailLabel,
      description: strings.admin.allowEssEditEmailDescription,
      value: Boolean(properties[ESS_BEHAVIOUR_KEYS.allowSelfEditEmail]),
      type: 'boolean',
      isSection: false
    },
    {
      id: ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber,
      label: strings.admin.allowEssEditPhoneNumberLabel,
      description: strings.admin.allowEssEditPhoneNumberDescription,
      value: Boolean(properties[ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber]),
      type: 'boolean',
      isSection: false
    },
    {
      id: 'section-holiday-settings',
      label: strings.admin.sectionHolidaySettings,
      description: strings.admin.sectionHolidaySettingsDescription,
      isSection: true
    },
    {
      id: 'defaultHolidayRegionCode',
      label: strings.admin.defaultHolidayRegionCodeLabel,
      description: strings.admin.defaultHolidayRegionCodeDescription,
      value: data.defaultHolidayRegionCode ?? undefined,
      type: 'select',
      options: holidayRegionOptions,
      isSection: false
    },
    {
      id: 'section-leave-settings',
      label: strings.admin.sectionLeaveSettings,
      description: strings.admin.sectionLeaveSettingsDescription,
      isSection: true
    },
    {
      id: 'leaveDaysCarriedOver',
      label: strings.admin.leaveCarryOver,
      description: strings.admin.leaveCarryOverDescription,
      value: data.leaveDaysCarriedOver ?? 0,
      type: 'number',
      numberConfig: {
        min: 0,
        step: 1,
        unit: strings.common.days,
        integerOnly: true
      },
      isSection: false
    },
    {
      id: 'leaveYearStartMonth',
      label: strings.admin.leaveYearStartMonthLabel,
      description: strings.admin.leaveYearStartMonthDescription,
      value: data.leaveYearStartMonth ?? 1,
      type: 'select',
      options: LEAVE_YEAR_START_MONTH_OPTIONS,
      isSection: false
    },
    {
      id: 'section-optimisation-settings',
      label: strings.admin.sectionOptimisationSettings,
      description: strings.admin.sectionOptimisationSettingsDescription,
      isSection: true
    },
    {
      id: OPTIMISER_SOLVER_KEYS.enabledSolvers,
      label: strings.admin.enabledSolversLabel,
      description: strings.admin.enabledSolversDescription,
      value: getOptimiserEnabledSolvers(properties),
      type: 'select',
      options: [
        { value: 'geneticAlgorithm', label: strings.admin.enabledSolversGeneticAlgorithm },
        { value: 'cpSat', label: strings.admin.enabledSolversCpSat },
        { value: 'both', label: strings.admin.enabledSolversBoth }
      ],
      isSection: false
    },
    {
      id: OPTIMISER_SOLVER_KEYS.showEvaluation,
      label: strings.admin.optimisationEvaluationLabel,
      description: strings.admin.optimisationEvaluationDescription,
      value: Boolean(properties[OPTIMISER_SOLVER_KEYS.showEvaluation]),
      type: 'boolean',
      isSection: false
    }
  ];

  return rows.filter(({ superAdminOnly }) => !superAdminOnly || superAdmin);
};

// ---------------------------------------
// Map back to API model
// ---------------------------------------
const mapFromRowData = (snapshot: AdminSettings, rowData: SettingsGridRow[]): AdminSettings => {
  const getValue = (id: string) => rowData.find(r => r.id === id)?.value as string | number | undefined;

  const leaveTypeTier = getValue('leaveTypeTier') as number;
  const contractTier = getValue('contractTier') as number;
  const defaultHolidayRegionCode = getValue('defaultHolidayRegionCode') as string;
  const leaveDaysCarriedOver = getValue('leaveDaysCarriedOver') as number | undefined;
  const leaveYearStartMonth = getValue('leaveYearStartMonth') as number | undefined;
  const allowSelfEditEmail = rowData.find(r => r.id === ESS_BEHAVIOUR_KEYS.allowSelfEditEmail)?.value;
  const allowSelfEditPhoneNumber = rowData.find(r => r.id === ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber)?.value;
  const allowEssWorkingPatterns = rowData.find(r => r.id === ROSTER_SETTINGS_KEYS.allowEssWorkingPatterns)?.value;
  const allowEssAvailabilityAdjustment = rowData.find(
    r => r.id === ROSTER_SETTINGS_KEYS.allowEssAvailabilityAdjustment
  )?.value;
  const enabledSolvers = rowData.find(r => r.id === OPTIMISER_SOLVER_KEYS.enabledSolvers)?.value;
  const showEvaluation = rowData.find(r => r.id === OPTIMISER_SOLVER_KEYS.showEvaluation)?.value;
  const minuteIncrement = rowData.find(r => r.id === MINUTE_INCREMENT_KEY)?.value;

  if (
    defaultHolidayRegionCode === undefined ||
    typeof allowSelfEditEmail !== 'boolean' ||
    typeof allowSelfEditPhoneNumber !== 'boolean' ||
    typeof allowEssWorkingPatterns !== 'boolean' ||
    typeof allowEssAvailabilityAdjustment !== 'boolean' ||
    typeof showEvaluation !== 'boolean' ||
    typeof minuteIncrement !== 'number' ||
    !MINUTE_INCREMENT_OPTIONS.includes(minuteIncrement) ||
    (enabledSolvers !== 'geneticAlgorithm' && enabledSolvers !== 'cpSat' && enabledSolvers !== 'both')
  ) {
    return snapshot;
  }

  const properties = parseProperties(snapshot.properties);
  properties[ESS_BEHAVIOUR_KEYS.allowSelfEditEmail] = allowSelfEditEmail;
  properties[ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber] = allowSelfEditPhoneNumber;
  properties[ROSTER_SETTINGS_KEYS.allowEssWorkingPatterns] = allowEssWorkingPatterns;
  properties[ROSTER_SETTINGS_KEYS.allowEssAvailabilityAdjustment] = allowEssAvailabilityAdjustment;
  properties[OPTIMISER_SOLVER_KEYS.enabledSolvers] = enabledSolvers;
  properties[OPTIMISER_SOLVER_KEYS.showEvaluation] = showEvaluation;
  properties[MINUTE_INCREMENT_KEY] = minuteIncrement;
  delete properties[OPTIMISER_SOLVER_KEYS.enableGeneticAlgorithm];
  delete properties[OPTIMISER_SOLVER_KEYS.enableCpSat];

  return {
    ...snapshot,
    ...(leaveTypeTier !== undefined && { leaveTypeTier }),
    ...(contractTier !== undefined && { contractTier }),
    defaultHolidayRegionCode,
    leaveDaysCarriedOver: leaveDaysCarriedOver ?? 0,
    leaveYearStartMonth: leaveYearStartMonth ?? 1,
    properties: JSON.stringify(properties)
  };
};

// ---------------------------------------
// AdminSettingsGrid Component
// ---------------------------------------
export const AdminSettingsGrid: React.FC = () => {
  const [user, getEffectiveTenantId, isDelegatedSuperAdmin] = useAuthenticationService(s => [
    s.user,
    s.getEffectiveTenantId,
    s.isDelegatedSuperAdmin
  ]);

  const [
    {
      data: supportedHolidayRegionsData,
      error: supportedHolidayRegionsError,
      fetching: supportedHolidayRegionsFetching
    }
  ] = useGetSupportedHolidayRegionsQuery();
  if (supportedHolidayRegionsError) handleError(supportedHolidayRegionsError);

  const [snapshot, setSnapshot] = useState<AdminSettings>();
  const [rowData, setRowData] = useState<SettingsGridRow[]>([]);
  const [initialRowData, setInitialRowData] = useState<SettingsGridRow[]>([]);

  const apiUrl = useVariable('apiUrl')?.replace('/graphql', '');
  const tenantId = getEffectiveTenantId();
  const holidayRegionOptions = useMemo<OptionItem[]>(
    () =>
      (supportedHolidayRegionsData?.supportedHolidayRegions ?? [])
        .filter((region): region is NonNullable<typeof region> => region != null)
        .map(region => ({
          label: region.displayName,
          value: region.code
        })),
    [supportedHolidayRegionsData]
  );

  const isDirty = isRowDataDirty(rowData, initialRowData);

  const organisationSettingsReadUrl = `${apiUrl}/api/admin/organisation-settings`;
  const organisationSettingsWriteUrl = `${apiUrl}/api/admin/organisation-settings`;

  const createRequestInit = () => {
    if (!user?.access_token || !tenantId) return null;

    return {
      headers: getAuthenticatedHeaders()
    };
  };

  useEffect(() => {
    const requestInit = createRequestInit();
    if (!requestInit) return;

    const load = async () => {
      const res = await fetch(organisationSettingsReadUrl, requestInit);
      const data = await res.json();
      const mapped = mapOrganisationSettingsToRowData(data, holidayRegionOptions, isDelegatedSuperAdmin);

      setSnapshot(data);
      setRowData(mapped);
      setInitialRowData(mapped.map(r => ({ ...r })));
    };

    load();
  }, [user, apiUrl, tenantId, holidayRegionOptions, isDelegatedSuperAdmin]);

  useEffect(() => {
    if (!snapshot || supportedHolidayRegionsFetching) return;

    const mapped = mapOrganisationSettingsToRowData(snapshot, holidayRegionOptions, isDelegatedSuperAdmin);
    setRowData(mapped);
    setInitialRowData(mapped.map(r => ({ ...r })));
  }, [snapshot, holidayRegionOptions, supportedHolidayRegionsFetching, isDelegatedSuperAdmin]);

  const handleSaveChanges = async () => {
    const requestInit = createRequestInit();
    if (!requestInit || !snapshot) return;

    const mapped = mapFromRowData(snapshot, rowData);

    await fetch(organisationSettingsWriteUrl, {
      method: 'PUT',
      ...requestInit,
      body: JSON.stringify(mapped)
    });

    setSnapshot(mapped);
    setInitialRowData(rowData.map(r => ({ ...r })));
  };

  return (
    <GenericSettingsGrid rowData={rowData} onRowDataChange={setRowData} onSave={handleSaveChanges} isDirty={isDirty} />
  );
};

export default AdminSettingsGrid;
